import React, { memo, useState, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import { Input } from 'components/form';
import SelectionCheckbox from 'components/form/SelectionCheckbox';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { Option } from 'lib/models/option';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { Footer } from '../Footer/Footer.component';
import { Inputs } from '..';
import MobileHeaderMp from '../MobileHeaderMp';
import SidebarMP from '../SidebarMP';

import styles from '../Onboarding.module.scss';

export interface ResponsibilitiesProps {
  /** Custom class name */
  className?: string;
  isCandidateOnboardingPage?: boolean;
  areaOfResponsibilities: Array<Option> | undefined;
  candidateResponsibilities: Array<Inputs>;
  onNextClick: ({
    selectedResponsibilities,
    isBackClicked,
  }: {
    selectedResponsibilities: Array<Inputs>;
    isBackClicked?: boolean;
  }) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  isMatchingPreferences?: boolean;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const Responsibilities: React.FC<ResponsibilitiesProps> = memo(
  ({
    className = '', // custom class name
    areaOfResponsibilities,
    candidateResponsibilities,
    onNextClick,
    isLoading,
    isBackLoading,
    isMatchingPreferences,
    onStepSelect,
  }: ResponsibilitiesProps) => {
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInterests } = useCandidate();
    const [t] = useTranslation('candidate-onboarding');
    const [tCommon] = useTranslation('common');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    const contentClassNames = cn(styles.container, className);
    const [search, setSearch] = useState('');

    const [selectedResponsibilities, setSelectedResponsibilities] = useState<
      Array<Inputs>
    >([]);

    useEffect(() => {
      if (candidateResponsibilities)
        setSelectedResponsibilities(candidateResponsibilities);
    }, [candidateResponsibilities]);
    const isReady = !!selectedResponsibilities.length;
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding({ isReady });

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick({ selectedResponsibilities, isBackClicked });
    };

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const apiCallToUpdate = async (modifiedResponsibilities: Array<Inputs>) => {
      try {
        await updateCandidateInterests({
          selectedInterests: modifiedResponsibilities,
          interestable_type: 'AreaOfResponsibility',
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedResponsibilities) => {
        await apiCallToUpdate(modifiedResponsibilities);
      }, 3000)
    ).current;

    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };

    const onResponsibilitiesCheckboxClick = (id: string) => {
      setValueEdited(true);
      const index = selectedResponsibilities?.findIndex(
        (interest) => interest.interestable_id === id
      );
      let modifiedResponsibilities = [];
      if (index === -1) {
        modifiedResponsibilities = [
          ...selectedResponsibilities,
          {
            interestable_id: id,
            interestable_type: 'AreaOfResponsibility',
            weight: 1,
          },
        ];
      } else {
        modifiedResponsibilities =
          selectedResponsibilities &&
          selectedResponsibilities.filter(
            (item) => item.interestable_id !== id
          );
      }

      /** For MP
       * Desktop: Autosave on every change
       *          Do not make changes when nothing is selected on mandatory step
       * Mobile: Save on CTA click
       */
      setSelectedResponsibilities(modifiedResponsibilities);
      if (modifiedResponsibilities.length === 0) return;

      if (isMatchingPreferences && !screens.sm) {
        debouncedAPICall(modifiedResponsibilities);
      }
    };

    const trimmedSearch = search.trim();
    const filteredOptions = useMemo(() => {
      return search
        ? areaOfResponsibilities?.filter(
            (resp) =>
              resp.label.toLowerCase().indexOf(trimmedSearch.toLowerCase()) > -1
          )
        : areaOfResponsibilities;
    }, [trimmedSearch, areaOfResponsibilities, search]);

    if (!areaOfResponsibilities) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }
    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.role-types')}
          onChevronClick={onChevronClick}
        />
        <div className={contentClassNames} data-testid="Responsibilities">
          {isMatchingPreferences && onStepSelect && (
            <SidebarMP
              className={styles.sidebar}
              isReady={isReady}
              onStepSelect={onStepSelect}
            />
          )}
          <div
            className={cn(styles.content, {
              [styles['content-mp']]: isMatchingPreferences,
            })}
          >
            <p className={styles.setup}>{t('roles.title')}</p>
            <p className={styles.searchDesc}>
              {t('roles.subtitle1')}
              <mark>{t('roles.subtitle2')}</mark>
              {t('roles.subtitle3')}
            </p>
            <Input
              className={cn(styles.search)}
              placeholder={tCommon('search')}
              startIcon="search"
              startIconSize="small"
              endIconSize="xsmall"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              endIcon={search ? 'cancel' : undefined}
              onEndIconClick={() => setSearch('')}
            />
            <div className={styles.checkboxesResponsibility}>
              {filteredOptions?.map((responsibility) => (
                <SelectionCheckbox
                  key={responsibility.id}
                  checked={selectedResponsibilities?.some(
                    ({ interestable_id }) =>
                      responsibility.id === interestable_id
                  )}
                  onChange={() =>
                    onResponsibilitiesCheckboxClick(responsibility.id)
                  }
                  className={styles.checkboxResponsibility}
                >
                  {responsibility.label}
                </SelectionCheckbox>
              ))}
            </div>
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>
          <Footer
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            isReady={isReady}
            onSubmit={onSubmit}
            isWindowScrolled={isWindowScrolled}
            isFullyScrolled={fullyScrolled}
            isMatchingPreference={isMatchingPreferences}
          />
        </div>
      </div>
    );
  }
);

Responsibilities.displayName = 'Responsibilities';
